import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { adminWalletService, type TopupTier } from '../../services/walletService';

const { Title, Text } = Typography;

const fmt = (n: number) => `SGD ${Number(n).toFixed(2)}`;

const WalletTierManagement: React.FC = () => {
  const [tiers, setTiers] = useState<TopupTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<TopupTier | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const list = await adminWalletService.listTiers();
      setTiers(list);
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to load tiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true, sortOrder: 0 });
    setModalOpen(true);
  };

  const openEdit = (tier: TopupTier) => {
    setEditing(tier);
    form.setFieldsValue({
      name: tier.Name,
      payAmount: Number(tier.PayAmount),
      creditAmount: Number(tier.CreditAmount),
      isActive: tier.IsActive,
      sortOrder: tier.SortOrder
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (values.creditAmount < values.payAmount) {
        message.error('Credit amount must be greater than or equal to pay amount');
        return;
      }
      if (editing) {
        await adminWalletService.updateTier(editing.TierID, values);
        message.success('Tier updated');
      } else {
        await adminWalletService.createTier(values);
        message.success('Tier created');
      }
      setModalOpen(false);
      load();
    } catch (err: any) {
      if (err?.errorFields) return; // form validation errors are shown inline
      message.error(err?.response?.data?.message || 'Failed to save tier');
    }
  };

  const handleDeactivate = async (tierId: string) => {
    try {
      await adminWalletService.deactivateTier(tierId);
      message.success('Tier deactivated');
      load();
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Failed to deactivate tier');
    }
  };

  const columns = [
    { title: 'Name', dataIndex: 'Name', key: 'Name' },
    {
      title: 'Customer Pays', dataIndex: 'PayAmount', key: 'PayAmount', align: 'right' as const,
      render: (v: number) => fmt(v)
    },
    {
      title: 'Credit', dataIndex: 'CreditAmount', key: 'CreditAmount', align: 'right' as const,
      render: (v: number) => fmt(v)
    },
    {
      title: 'Bonus', key: 'Bonus', align: 'right' as const,
      render: (_: any, r: TopupTier) => {
        const bonus = Number(r.CreditAmount) - Number(r.PayAmount);
        return bonus > 0 ? <Tag color="green">{fmt(bonus)}</Tag> : <Text type="secondary">—</Text>;
      }
    },
    {
      title: 'Sort', dataIndex: 'SortOrder', key: 'SortOrder', align: 'right' as const, width: 80
    },
    {
      title: 'Status', dataIndex: 'IsActive', key: 'IsActive',
      render: (v: boolean) => v ? <Tag color="blue">Active</Tag> : <Tag>Inactive</Tag>
    },
    {
      title: 'Actions', key: 'actions', width: 180,
      render: (_: any, r: TopupTier) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>Edit</Button>
          {r.IsActive && (
            <Popconfirm
              title="Deactivate this tier?"
              description="Customers will no longer see this tier. Past top-ups are unaffected."
              onConfirm={() => handleDeactivate(r.TierID)}
              okText="Deactivate"
              cancelText="Cancel"
            >
              <Button size="small" danger icon={<DeleteOutlined />}>Deactivate</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Wallet Top-up Tiers</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Tier</Button>
      </Space>

      <Card>
        <Table
          rowKey="TierID"
          dataSource={tiers}
          columns={columns}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'No tiers defined. Click "New Tier" to add one.' }}
        />
      </Card>

      <Modal
        title={editing ? 'Edit Top-up Tier' : 'New Top-up Tier'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="name"
            label="Display Name"
            rules={[{ required: true, message: 'Name is required' }]}
            tooltip="What the customer sees, e.g. 'Top up SGD 100 — get SGD 120'"
          >
            <Input placeholder="Top up SGD 100 — get SGD 120" />
          </Form.Item>
          <Form.Item
            name="payAmount"
            label="Customer Pays (SGD)"
            rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0.01, message: 'Must be > 0' }]}
          >
            <InputNumber min={0.01} step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="creditAmount"
            label="Credit to Wallet (SGD)"
            rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0.01, message: 'Must be > 0' }]}
            tooltip="Must be >= Customer Pays. Bonus = Credit − Pays."
          >
            <InputNumber min={0.01} step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="sortOrder" label="Sort Order">
            <InputNumber step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WalletTierManagement;
